import { useEffect, useRef, forwardRef, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { logError } from "~src/utils/logger";
interface ChartWrapperProps {
  type: 'line' | 'area' | 'bar' | 'pie' | 'donut' | 'radialBar' | 'scatter' | 'bubble' | 'heatmap' | 'candlestick' | 'boxPlot' | 'radar' | 'polarArea' | 'rangeBar' | 'rangeArea' | 'treemap';
  series: ApexAxisChartSeries | ApexNonAxisChartSeries;
  options: ApexOptions;
  height?: string | number;
  width?: string | number;
}

/**
 * A wrapper component for ReactApexChart that handles resizing properly
 */
const ChartWrapper = forwardRef<HTMLDivElement, ChartWrapperProps>((props, ref) => {
  const { type, series, options, height } = props;
  const containerRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<boolean>(false);

  // Validate series data to prevent errors
  const validateSeries = (data: any) => {
    if (!data) return [];
    
    try {
      // For arrays (most chart types)
      if (Array.isArray(data)) {
        return data.map(series => {
          // Make sure each series has proper data
          if (!series) return { name: '', data: [] };
          
          // Validate data property
          if (!series.data || !Array.isArray(series.data)) {
            return { ...series, data: [] };
          }
          
          // Keep valid series as is
          return series;
        });
      }
      // Return original data if it's not an array
      return data;
    } catch (err) {
      logError(`'Error validating chart series:': ${err}`);
      return [];
    }
  };

  // Sync refs
  useEffect(() => {
    if (ref && containerRef.current) {
      if (typeof ref === 'function') {
        ref(containerRef.current);
      } else {
        ref.current = containerRef.current;
      }
    }
  }, [ref]);

  // Force chart to resize when container size changes
  useEffect(() => {
    if (!containerRef.current) return;

    // Create a ResizeObserver to watch for container size changes
    const resizeObserver = new ResizeObserver(() => {
      // Force a resize event to make the chart redraw
      window.dispatchEvent(new Event('resize'));
    });

    // Start observing the container
    resizeObserver.observe(containerRef.current);

    // Clean up
    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // Force initial render
  useEffect(() => {
    // Trigger a resize event after a short delay to ensure the chart is rendered
    const timer = setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  // Add chart events to handle resizing
  const chartOptions = {
    ...options,
    chart: {
      ...options.chart,
      events: {
        ...options.chart?.events,
        mounted: function(chart: any) {
          // Force resize after chart is mounted
          setTimeout(() => {
            try {
              chart.windowResizeHandler();
            } catch (err) {
              logError(`'Error handling chart resize:': ${err}`);
            }
          }, 0);
          
          // Call original mounted event if it exists
          if (options.chart?.events?.mounted) {
            try {
              options.chart.events.mounted(chart);
            } catch (err) {
              logError(`'Error in chart mounted event:': ${err}`);
            }
          }
        }
      }
    }
  };

  // Handle errors in chart rendering
  const handleChartError = () => {
    logError('Error rendering chart');
    setError(true);
  };

  if (error) {
    return (
      <div 
        ref={containerRef}
        style={{ 
          width: '100%', 
          height: height || '300px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f8f9fa',
          color: '#6c757d',
          fontSize: '14px',
          borderRadius: '4px'
        }}
      >
        Chart data cannot be displayed
      </div>
    );
  }

  return (
    <div 
      ref={containerRef} 
      style={{ 
        width: '100%', 
        minWidth: 0,
        display: 'block',
        position: 'relative'
      }}
    >
      <ReactApexChart
        type={type}
        series={validateSeries(series)}
        options={chartOptions}
        height={height}
        width="100%"
        onError={handleChartError}
      />
    </div>
  );
});

// Add display name for better debugging
ChartWrapper.displayName = 'ChartWrapper';

export default ChartWrapper; 